import React, { useState, useRef, useEffect } from 'react';
import { X, Download, Save, RotateCcw } from 'lucide-react';

function SealGenerator({ onSave, onClose, defaultText = '' }) {
  const canvasRef = useRef(null);
  const [text, setText] = useState(defaultText || '');
  const [subText, setSubText] = useState('인');
  const [sealStyle, setSealStyle] = useState('classic'); // classic, traditional, modern, vintage
  const [shape, setShape] = useState('circle'); // circle, square, oval, rounded
  const [color, setColor] = useState('#c41e3a'); // 진한 주홍색
  const [fontSize, setFontSize] = useState(28);
  const [borderWidth, setBorderWidth] = useState(3);
  const [texture, setTexture] = useState(true); // 질감 효과

  const sealStyles = [
    { id: 'classic', label: '정통', desc: '이중 테두리' },
    { id: 'traditional', label: '전각', desc: '음각 스타일' },
    { id: 'modern', label: '모던', desc: '깔끔한 디자인' },
    { id: 'vintage', label: '빈티지', desc: '고풍스러운' }
  ];

  const shapes = [
    { id: 'circle', label: '원형' },
    { id: 'square', label: '사각' },
    { id: 'rounded', label: '둥근사각' },
    { id: 'oval', label: '타원' }
  ];

  const colors = [
    { id: '#c41e3a', label: '주홍', name: '전통 주홍' },
    { id: '#8b0000', label: '암적', name: '암적색' },
    { id: '#1a1a2e', label: '먹색', name: '먹색' },
    { id: '#2d5a7b', label: '감청', name: '감청색' },
    { id: '#4a3728', label: '갈색', name: '고동색' }
  ];

  useEffect(() => {
    drawSeal();
  }, [text, subText, sealStyle, shape, color, fontSize, borderWidth, texture]);

  const drawSeal = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const size = 200;
    const center = size / 2;

    // 캔버스 초기화 (투명 배경)
    ctx.clearRect(0, 0, size, size);

    // 도장 색상 설정
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = borderWidth;

    const margin = borderWidth + 8;
    const innerMargin = margin + 6;

    // 도형 그리기
    if (sealStyle === 'classic') {
      // 이중 테두리 스타일
      drawShape(ctx, shape, center, size, margin, borderWidth);
      ctx.lineWidth = borderWidth * 0.6;
      drawShape(ctx, shape, center, size, innerMargin + 4, borderWidth * 0.6);
    } else if (sealStyle === 'traditional') {
      // 전각 스타일 - 두꺼운 테두리
      ctx.lineWidth = borderWidth * 1.5;
      drawShape(ctx, shape, center, size, margin, borderWidth * 1.5);
    } else if (sealStyle === 'modern') {
      // 모던 - 얇은 단일 테두리
      ctx.lineWidth = borderWidth * 0.8;
      drawShape(ctx, shape, center, size, margin + 2, borderWidth * 0.8);
    } else if (sealStyle === 'vintage') {
      // 빈티지 - 이중 테두리 + 장식
      drawShape(ctx, shape, center, size, margin, borderWidth);
      ctx.lineWidth = borderWidth * 0.4;
      drawShape(ctx, shape, center, size, innerMargin + 8, borderWidth * 0.4);

      // 모서리 장식 (사각형일 경우)
      if (shape === 'square' || shape === 'rounded') {
        drawCornerDecorations(ctx, size, margin, color);
      }
    }

    // 텍스트 설정
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 전각 스타일은 배경 채움
    if (sealStyle === 'traditional') {
      drawTextWithIntaglio(ctx, text, subText, center, size, margin, color, fontSize);
    } else {
      drawTextNormal(ctx, text, subText, center, fontSize, sealStyle);
    }

    // 질감 효과 추가
    if (texture) {
      addTexture(ctx, size, color);
    }
  };

  const drawShape = (ctx, shapeType, center, size, margin, lineWidth) => {
    ctx.beginPath();
    if (shapeType === 'circle') {
      ctx.arc(center, center, center - margin, 0, Math.PI * 2);
    } else if (shapeType === 'square') {
      ctx.rect(margin, margin, size - margin * 2, size - margin * 2);
    } else if (shapeType === 'rounded') {
      const radius = 15;
      const x = margin, y = margin, w = size - margin * 2, h = size - margin * 2;
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
    } else if (shapeType === 'oval') {
      ctx.ellipse(center, center, center - margin, (center - margin) * 0.75, 0, 0, Math.PI * 2);
    }
    ctx.stroke();
  };

  const drawCornerDecorations = (ctx, size, margin, color) => {
    const decoSize = 8;
    const offset = margin + 2;
    ctx.fillStyle = color;

    // 네 모서리에 작은 장식
    [[offset, offset], [size - offset - decoSize, offset],
     [offset, size - offset - decoSize], [size - offset - decoSize, size - offset - decoSize]].forEach(([x, y]) => {
      ctx.fillRect(x, y, decoSize, 2);
      ctx.fillRect(x, y, 2, decoSize);
    });
  };

  const drawTextWithIntaglio = (ctx, mainText, sub, center, size, margin, color, baseFontSize) => {
    // 전각(음각) 스타일 - 배경을 채우고 글자를 투명하게
    const innerSize = size - margin * 2 - 10;

    // 임시 캔버스로 마스킹 효과
    ctx.save();

    // 배경 원/사각형 그리기 (반투명)
    ctx.globalAlpha = 0.15;
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(center, center, innerSize / 2 - 5, 0, Math.PI * 2);
    } else {
      ctx.rect(margin + 10, margin + 10, size - margin * 2 - 20, size - margin * 2 - 20);
    }
    ctx.fill();

    ctx.globalAlpha = 1;
    ctx.restore();

    // 텍스트는 일반 스타일로
    drawTextNormal(ctx, mainText, sub, center, baseFontSize + 2, 'traditional');
  };

  const drawTextNormal = (ctx, mainText, sub, center, baseFontSize, style) => {
    if (!mainText) return;

    const fontFamily = "'Noto Serif KR', 'Batang', serif";

    if (mainText.length === 1) {
      // 한 글자: 크게 중앙에
      ctx.font = `bold ${baseFontSize + 30}px ${fontFamily}`;
      ctx.fillText(mainText, center, center - 5);
      if (sub) {
        ctx.font = `bold ${baseFontSize - 2}px ${fontFamily}`;
        ctx.fillText(sub, center, center + 45);
      }
    } else if (mainText.length === 2) {
      // 두 글자: 세로 배치
      ctx.font = `bold ${baseFontSize + 16}px ${fontFamily}`;
      ctx.fillText(mainText[0], center, center - 28);
      ctx.fillText(mainText[1], center, center + 28);
    } else if (mainText.length === 3) {
      // 세 글자: 위 1, 아래 2 또는 세로 배치
      ctx.font = `bold ${baseFontSize + 8}px ${fontFamily}`;
      ctx.fillText(mainText[0], center, center - 35);
      ctx.fillText(mainText[1], center - 25, center + 20);
      ctx.fillText(mainText[2], center + 25, center + 20);
    } else if (mainText.length === 4) {
      // 네 글자: 2x2 배치 (전통 낙관 스타일)
      ctx.font = `bold ${baseFontSize + 4}px ${fontFamily}`;
      ctx.fillText(mainText[0], center - 25, center - 25);
      ctx.fillText(mainText[1], center + 25, center - 25);
      ctx.fillText(mainText[2], center - 25, center + 25);
      ctx.fillText(mainText[3], center + 25, center + 25);
    } else {
      // 다섯 글자 이상: 2줄 또는 작은 글씨
      ctx.font = `bold ${baseFontSize - 4}px ${fontFamily}`;
      const mid = Math.ceil(mainText.length / 2);
      ctx.fillText(mainText.slice(0, mid), center, center - 18);
      ctx.fillText(mainText.slice(mid), center, center + 18);
      if (sub) {
        ctx.font = `bold ${baseFontSize - 8}px ${fontFamily}`;
        ctx.fillText(sub, center, center + 50);
      }
    }
  };

  const addTexture = (ctx, size, baseColor) => {
    // 미세한 노이즈 효과로 도장 찍힌 느낌
    const imageData = ctx.getImageData(0, 0, size, size);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] > 0) { // 투명하지 않은 픽셀만
        const noise = (Math.random() - 0.5) * 30;
        // 약간의 불균일함 추가
        if (Math.random() > 0.97) {
          data[i + 3] = Math.max(0, data[i + 3] - Math.random() * 80);
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `seal_${Date.now()}.png`, { type: 'image/png' });
        onSave(file, canvas.toDataURL('image/png'));
      }
    }, 'image/png');
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `인감_${text || '도장'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleReset = () => {
    setText('');
    setSubText('인');
    setSealStyle('classic');
    setShape('circle');
    setColor('#c41e3a');
    setFontSize(28);
    setBorderWidth(3);
    setTexture(true);
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      backgroundColor: '#fefefe',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '480px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)'
    },
    header: {
      padding: '20px 24px',
      borderBottom: '1px solid #e8e8e8',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #faf8f5 0%, #f5f0e8 100%)'
    },
    title: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#2d2d2d',
      fontFamily: "'Noto Serif KR', serif"
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '8px',
      display: 'flex'
    },
    body: {
      padding: '24px',
      backgroundColor: '#faf8f5'
    },
    previewSection: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '24px'
    },
    canvasWrapper: {
      padding: '24px',
      backgroundColor: '#fff',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: '1px solid #e8e8e8'
    },
    canvas: {
      display: 'block'
    },
    section: {
      marginBottom: '18px'
    },
    label: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#4a4a4a',
      marginBottom: '8px',
      display: 'block'
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '15px',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: "'Noto Serif KR', serif",
      backgroundColor: '#fff'
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 100px',
      gap: '10px'
    },
    optionGroup: {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap'
    },
    styleBtn: {
      padding: '10px 12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#fff',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      transition: 'all 0.2s',
      textAlign: 'center',
      minWidth: '70px'
    },
    styleBtnActive: {
      borderColor: '#c41e3a',
      backgroundColor: '#fef2f2',
      color: '#c41e3a'
    },
    optionBtn: {
      padding: '8px 14px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      backgroundColor: '#fff',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    optionBtnActive: {
      borderColor: '#c41e3a',
      backgroundColor: '#fef2f2',
      color: '#c41e3a'
    },
    colorBtn: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      border: '2px solid #e8e8e8',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    colorBtnActive: {
      borderColor: '#2d2d2d',
      transform: 'scale(1.15)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      appearance: 'none',
      backgroundColor: '#e8e8e8',
      outline: 'none'
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    actions: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px'
    },
    btn: {
      flex: 1,
      padding: '12px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      border: 'none',
      transition: 'all 0.2s'
    },
    primaryBtn: {
      backgroundColor: '#c41e3a',
      color: '#fff'
    },
    secondaryBtn: {
      backgroundColor: '#fff',
      color: '#4a4a4a',
      border: '1px solid #ddd'
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>인감 도장 제작</h2>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={22} color="#666" />
          </button>
        </div>

        <div style={styles.body}>
          {/* 미리보기 */}
          <div style={styles.previewSection}>
            <div style={styles.canvasWrapper}>
              <canvas
                ref={canvasRef}
                width={200}
                height={200}
                style={styles.canvas}
              />
            </div>
          </div>

          {/* 도장 스타일 */}
          <div style={styles.section}>
            <label style={styles.label}>도장 스타일</label>
            <div style={styles.optionGroup}>
              {sealStyles.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSealStyle(s.id)}
                  style={{
                    ...styles.styleBtn,
                    ...(sealStyle === s.id ? styles.styleBtnActive : {})
                  }}
                  title={s.desc}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* 텍스트 입력 */}
          <div style={styles.section}>
            <label style={styles.label}>도장 내용</label>
            <div style={styles.row}>
              <input
                type="text"
                placeholder="이름 또는 상호명"
                value={text}
                onChange={(e) => setText(e.target.value)}
                style={styles.input}
                maxLength={8}
              />
              <input
                type="text"
                placeholder="인"
                value={subText}
                onChange={(e) => setSubText(e.target.value)}
                style={styles.input}
                maxLength={2}
              />
            </div>
          </div>

          {/* 도형 선택 */}
          <div style={styles.section}>
            <label style={styles.label}>형태</label>
            <div style={styles.optionGroup}>
              {shapes.map(s => (
                <button
                  key={s.id}
                  onClick={() => setShape(s.id)}
                  style={{
                    ...styles.optionBtn,
                    ...(shape === s.id ? styles.optionBtnActive : {})
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* 색상 선택 */}
          <div style={styles.section}>
            <label style={styles.label}>인주 색상</label>
            <div style={styles.optionGroup}>
              {colors.map(c => (
                <button
                  key={c.id}
                  onClick={() => setColor(c.id)}
                  style={{
                    ...styles.colorBtn,
                    backgroundColor: c.id,
                    ...(color === c.id ? styles.colorBtnActive : {})
                  }}
                  title={c.name}
                />
              ))}
            </div>
          </div>

          {/* 크기 조절 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={styles.section}>
              <label style={styles.label}>글자 크기</label>
              <input
                type="range"
                min="20"
                max="36"
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
                style={styles.slider}
              />
            </div>
            <div style={styles.section}>
              <label style={styles.label}>테두리</label>
              <input
                type="range"
                min="2"
                max="6"
                value={borderWidth}
                onChange={(e) => setBorderWidth(parseInt(e.target.value))}
                style={styles.slider}
              />
            </div>
          </div>

          {/* 질감 효과 */}
          <div style={styles.section}>
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={texture}
                onChange={(e) => setTexture(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={{ fontSize: '13px', color: '#4a4a4a' }}>도장 찍힘 효과 (자연스러운 질감)</span>
            </label>
          </div>

          {/* 버튼 */}
          <div style={styles.actions}>
            <button onClick={handleReset} style={{ ...styles.btn, ...styles.secondaryBtn }}>
              <RotateCcw size={16} /> 초기화
            </button>
            <button onClick={handleDownload} style={{ ...styles.btn, ...styles.secondaryBtn }}>
              <Download size={16} /> 다운로드
            </button>
            <button onClick={handleSave} style={{ ...styles.btn, ...styles.primaryBtn }}>
              <Save size={16} /> 저장
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SealGenerator;
